import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, MailPlus } from 'lucide-react'
import { api } from '@/util/api'
import { Button, Form, Notice, TextField } from '@/components/play'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'

/** A code someone was given goes straight to sign-up with it: its own form, beside the waitlist's rather than inside it. */
function InviteCodeForm() {
  const navigate = useNavigate()
  const [inviteCode, setInviteCode] = useState('')

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const trimmedCode = inviteCode.trim()
    if (!trimmedCode) return
    navigate(`/signup?invite_code=${encodeURIComponent(trimmedCode)}`)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <TextField
        label="Invite code"
        autoComplete="off"
        placeholder="Paste your invite code"
        value={inviteCode}
        onChange={(event) => setInviteCode(event.target.value)}
      />
      <Button type="submit" variant="secondary" className="w-full" disabled={!inviteCode.trim()}>
        Continue to sign-up
        <ArrowRight aria-hidden="true" />
      </Button>
    </form>
  )
}

export function PrivateBetaWaitlistPage() {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [mainUseCase, setMainUseCase] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const invalidInviteCode = searchParams.get('invalid_code') === '1'
  const joinedNotice = useRef<HTMLDivElement>(null)

  // The form, and the button that sent it, give way to the answer; focus follows so it isn't lost.
  useEffect(() => {
    if (submitted) joinedNotice.current?.focus()
  }, [submitted])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await api.joinPrivateBetaWaitlist({
        email,
        main_use_case: mainUseCase,
      })

      setSuccessMessage(response?.data?.message || "You're on the waitlist. We'll reach out soon.")
      setSubmitted(true)
    } catch (err: any) {
      const apiErr = err?.response?.data?.error
      if (Array.isArray(apiErr?.errors)) {
        setError(apiErr.errors.join('\n'))
      } else {
        setError(apiErr?.message || "We couldn't add you to the waitlist. Try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <PublicAuthLayout
      eyebrow="Private beta"
      title="Request private beta access."
      description="Tell us about your event. We’ll invite teams as the beta grows."
      backLink={{ to: '/', label: 'StreamBridge' }}
    >
      <div className="space-y-6">
        {invalidInviteCode && (
          <Notice tone="error" alert>That invite code can’t be used. Ask for a new one, or join the waitlist.</Notice>
        )}

        {submitted ? (
          <Notice ref={joinedNotice} tabIndex={-1} tone="success">{successMessage}</Notice>
        ) : (
          // A Play Form: a missing entry is said beside its field, with focus there, not in the browser's bubble.
          <Form onSubmit={handleSubmit}>
            {error && (
              <Notice tone="error" alert>{error}</Notice>
            )}

            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              requiredMessage="Enter your email address."
              disabled={loading}
            />

            <TextField
              label="What will you livestream?"
              placeholder="A trail race with four cameras"
              value={mainUseCase}
              onChange={(event) => setMainUseCase(event.target.value)}
              required
              requiredMessage="Tell us what you’ll livestream."
              disabled={loading}
            />

            <Button type="submit" className="w-full" loading={loading}>
              <MailPlus aria-hidden="true" />
              {loading ? 'Joining' : 'Join private beta'}
            </Button>
          </Form>
        )}

        <p className="play-entry-or">or use an invite</p>
        <InviteCodeForm />

        <p className="text-center text-sm text-neutral-7">
          Have an account?{' '}
          <Link to="/login" className="font-semibold text-neutral-10 underline underline-offset-4">
            Sign in
          </Link>
        </p>
      </div>
    </PublicAuthLayout>
  )
}
